import { useEffect, useState } from "react";
import { GeneralCommonHeader } from "../../components/common/GeneralCommonHeader";
import {
  DISPLAY_HEADER_ICON_BACK,
  DISPLAY_HEADER_ICON_NONE,
  DISPLAY_PAGE_DISCLAIMER,
  SPLASH_DISPLAY_TIME,
  LANGUAGE_JAPANESE,
  LANGUAGE_ENGLISH,
} from "../../constants/common_constants";
import { calcUtcDatetime } from "../../utils/basis_util";
import { PiCheckSquare, PiSquare } from "react-icons/pi";
import { DisclaimerNextPageButton } from "../../features/components/disclaimer/ui_elements/DisclaimerNextPageButton";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IDisclaimerInfoType } from "../../features/components/disclaimer/types/disclaimer_types";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import { getDisclaimerInfo } from "../../features/api/dts_api_wrapper";
import i18n from "../../i18n/configs";

export const Disclaimer = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  //API用
  const { handleApi } = useApi();

  // 他のページから遷移された時の処理
  const location = useLocation();
  //トップ画面から遷移してきたかどうかを格納
  const fromTopPage: boolean = location.state?.isFromTop ?? false;
  //ログイン画面から遷移してきたかどうかを格納
  const fromLoginPage: boolean = location.state?.isFromLogin ?? false;

  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
    ) || "";
  const displayPage = DISPLAY_PAGE_DISCLAIMER;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  //同意したかどうかを格納
  const [consent, setConsent] = useState<boolean>(false);
  //免責事項情報を格納
  const [disclaimerInfo, setDisclaimerInfo] = useState<IDisclaimerInfoType>({
    disclaimer: "",
    version: "",
  });
  //言語をローカルストレージから取得してContextに格納
  const { setDacLanguage } = useDtsAppContext();
  const { dacLanguage } = useDtsAppContext();
  const language =
    localStorage.getItem("language") || navigator.language.split("_")[0];

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //スプラッシュ画面用Context
  const { splash, setSplash } = useDtsAppContext();

  // 利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });

  useEffect(() => {
    //免責事項画面の初回処理
    handleDisclaimerPageInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //ページ表示時に行うDisclaimerの処理
  const handleDisclaimerPageInit = async () => {
    try {
      console.log("handleDisclaimerPageInit start.");
      // 利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }

      // 言語切り替え
      if (language && language === LANGUAGE_JAPANESE) {
        setDacLanguage(language);
        localStorage.setItem("language", language);
        i18n.changeLanguage(language);
      } else {
        setDacLanguage(LANGUAGE_ENGLISH);
        localStorage.setItem("language", LANGUAGE_ENGLISH);
        i18n.changeLanguage(LANGUAGE_ENGLISH);
      }

      if (
        fromLoginPage &&
        (sessionStorage.getItem("splash") === null || splash)
      ) {
        //ログイン画面から遷移した かつ (スプラッシュ画面の表示フラグがセッションストレージから取れない(初回) または スプラッシュ画面の表示フラグがtrue)の場合,
        //免責事項情報の取得とスプラッシュ画面の処理を実行
        const promise = [handleGetDisclaimerInfo(), displaySplashScreen()];
        const responses = await Promise.all(promise);
        if (responses.every((result) => result)) {
          //Promise.allの結果が全部true(正常)だった場合, スプラッシュ画面の表示フラグをfalseにする
          setSplash(false);
          sessionStorage.setItem("splash", String(false));
        }
      } else {
        //その他(トップ画面から遷移した等)の場合, 免責事項情報の取得のみ(スプラッシュ画面の処理をしない)
        await handleGetDisclaimerInfo();
      }
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        if (fromTopPage) {
          //トップ画面から遷移した場合, トップ画面に戻る
          showErrorDialog(
            t("共通エラーポップ.エラー"),
            t("共通エラーポップ.通信エラーが発生しました。"),
            "navigateToTopPage"
          ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
        } else {
          showErrorDialog(
            t("共通エラーポップ.エラー"),
            t("共通エラーポップ.通信エラーが発生しました。"),
            "login"
          ); //エラー", "通信エラーが発生しました。(ログイン画面に遷移)
        }
      }
    }
  };

  //免責事項情報取得の処理(呼び出し元でのエラー処理必須)
  const handleGetDisclaimerInfo = async () => {
    //免責事項情報の取得
    const disclaimerInfoResult = await handleApi(
      getDisclaimerInfo,
      userId,
      localStorage.getItem("language")
    );
    if ("errorMessage" in disclaimerInfoResult) {
      console.log("errorMessage: ", disclaimerInfoResult.errorMessage);
      throw new Error("failed to get disclaimer info");
    }
    const disclaimerInfoStr = JSON.stringify(disclaimerInfoResult);
    const disclaimerInfoJson: IDisclaimerInfoType =
      JSON.parse(disclaimerInfoStr);
    //取得した免責事項情報をstateに格納
    setDisclaimerInfo(disclaimerInfoJson);
    //trueを返す
    return true;
  };

  //スプラッシュ画面の処理
  const displaySplashScreen = async () => {
    sessionStorage.setItem("splash", String(true));
    setSplash(true);
    await waitTimeMs(SPLASH_DISPLAY_TIME);
    //tureを返す
    return true;
  };
  // 非同期関数としてタイマーを実装
  const waitTimeMs = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  //同意のチェックボックスとボタンをレンダリングする関数(トップ画面から遷移時は表示しない)
  const renderAgreeCheckbox = () => {
    return (
      <div className="flex justify-center items-center mt-4">
        <div className="block">
          <div className="flex mb-3">
            <div
              className="px-2 cursor-pointer"
              onClick={() => setConsent((prevState) => !prevState)}
            >
              {consent ? (
                <PiCheckSquare size={"1.25rem"} />
              ) : (
                <PiSquare size={"1.25rem"} />
              )}
            </div>
            <p className="whitespace-nowrap">
              {t("免責事項画面.上記に同意します")}
            </p>
          </div>
          <div className="flex justify-center">
            <DisclaimerNextPageButton
              disabled={!consent}
              userId={userId}
              version={disclaimerInfo.version}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-svh overflow-hidden">
      {/* ヘッダー */}
      <GeneralCommonHeader
        title={t("免責事項画面.免責事項")}
        displayheaderIcon={
          fromTopPage ? DISPLAY_HEADER_ICON_BACK : DISPLAY_HEADER_ICON_NONE
        }
        onBtnClick={
          fromTopPage
            ? () => {
                navigate(-1);
              }
            : undefined
        }
      />
      {/* 画面 */}
      <div className="flex flex-col m-4 h-full overflow-hidden">
        {/* 免責事項表示 */}
        <textarea
          name="disclaimer"
          className="border border-black bg-white grow"
          disabled
          value={disclaimerInfo.disclaimer}
        ></textarea>
        {!fromTopPage && renderAgreeCheckbox()}
      </div>
    </div>
  );
};
